import React from 'react';
import styled, { css } from 'styled-components';

import { formatDateTime, isValidDate } from '~/util';

interface AbsoluteTimeProps {
  className?: string;
  timestamp: Date | string;
  preventHover?: boolean;
  alignment?: 'left' | 'right';
}

const Local = styled.div<{ alignment?: 'left' | 'right' }>`
  position: relative;
  opacity: 1;
  white-space: nowrap;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
      position: absolute;
    `}
`;

export const UTC = styled.div<{ alignment?: 'left' | 'right' }>`
  position: absolute;
  display: inline-block;
  top: 0;
  right: 0;
  white-space: nowrap;
  opacity: 0;
  z-index: 1;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  transition: opacity 0.2s;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
      position: relative;
    `}

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background-color: ${theme.secondary.blendToBackground(25)};
    `}

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 32px;
    background-image: linear-gradient(
      90deg,
      ${({ theme }) => theme.secondary.blendToBackground(50, 0)} 100%,
      ${({ theme }) => theme.secondary.blendToBackground(50, 1000)} 0%
    );

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        background-image: linear-gradient(
          90deg,
          ${theme.secondary.blendToBackground(25, 0)} 0%,
          ${theme.secondary.blendToBackground(25, 1000)} 100%
        );
      `}
  }

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      &:before {
        right: 0;
        left: auto;
        background-image: linear-gradient(
          -90deg,
          ${({ theme }) => theme.secondary.blendToBackground(50, 0)} 100%,
          ${({ theme }) => theme.secondary.blendToBackground(50, 1000)} 0%
        );

        ${({ theme }) =>
          theme.id !== 'default' &&
          css`
            background-image: linear-gradient(
              -90deg,
              ${theme.secondary.blendToBackground(25, 0)} 100%,
              ${theme.secondary.blendToBackground(25, 1000)} 0%
            );
          `}
      }
    `}
`;

const Wrapper = styled.div<{ preventHover?: boolean; alignment?: 'left' | 'right' }>`
  position: relative;
  font-size: 12px;
  line-height: 16px;
  height: 16px;
  display: inline-block;

  ${({ preventHover }) =>
    !preventHover &&
    css`
      &:hover {
        ${UTC} {
          opacity: 1;
          width: auto;
          overflow: visible;
          padding: 0 0 0 12px;
        }

        ${Local} {
          opacity: 0;
        }
      }
    `}

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      &:hover {
        ${UTC} {
          padding: 0;
        }
      }
    `};
`;

export const AbsoluteTime: React.FC<AbsoluteTimeProps> = ({
  className,
  timestamp,
  preventHover = false,
  alignment = 'right',
}) => {
  return (
    <Wrapper className={className} preventHover={preventHover} alignment={alignment}>
      {!isValidDate(timestamp) ? (
        '-'
      ) : (
        <>
          <Local alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'local' })}</Local>
          <UTC alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'utc' })}</UTC>
        </>
      )}
    </Wrapper>
  );
};
